import React, {useContext, useMemo, useState} from "react"
import './scss/App.scss'
import {
  QueryClient,
  QueryClientProvider,
} from "react-query"
import Content, {Profile} from "./Content"
import LocaleWrapper, {LocaleContext} from "./LocaleContext"
import {
  AuthCheck,
  GetContacts,
  GetInstanceLanguage, GetLanguageOptions, GetMessageSeenText, GetMessageWasSentToYou,
  GetProviderHasSeenYourMessageText, GetSentYouAMessage, GetSupportRoles,
  GetUser, GetUserGroupData,
  GetUserSurvey
} from "./API";
import {ReactComponent as Logo, ReactComponent as CloudLogo} from "./svg/logo-green.svg";
import {FormattedMessage, IntlProvider, useIntl} from "react-intl";
import {ReactComponent as Face} from "./svg/face.svg";
import _ from "lodash";

export const queryClient = new QueryClient()

const AuthContext = React.createContext({
  authContext: {},
  setAuthContext: () => {}
})

function AuthProvider({children, authContext}) {
  const [authData, setAuthData] = useState(authContext)
  const value = useMemo(() =>(
      {authData, setAuthData}
  ), [authData])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthData() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuthData must be used within an AuthProvider')
  }
  return context
}

const CurrentUserContext = React.createContext({
  currentUserContext: {},
  setCurrentUserContext: () => {}
})

function CurrentUserProvider({children, userContext}) {
  const [currentUserData, setCurrentUserData] = useState(userContext)
  const value = useMemo(() =>(
      {currentUserData, setCurrentUserData}
  ), [currentUserData])
  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>
}

export function useCurrentUserData() {
  const context = useContext(CurrentUserContext)
  if (context === undefined) {
    throw new Error('useCurrentUserData must be used within an CurrentUserProvider')
  }
  return context
}

const LanguageContext = React.createContext({
  languageContext: {},
  setLanguageContext: () => {}
})

function LanguageProvider({children, context}) {
  const [instanceLang, setInstanceLang] = useState(context)
  const value = useMemo(() =>(
      {instanceLang, setInstanceLang}
  ), [instanceLang])
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguageData() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguageData must be used within a LanguageProvider')
  }
  return context
}

const ConfigContext = React.createContext({
  configContext: {},
  setConfigContext: () => {}
})

function ConfigProvider({children, context}) {
  const [configData, setConfigData] = useState(context)
  const value = useMemo(() =>(
      {configData, setConfigData}
  ), [configData])
  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
}

export function useConfigData() {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error('useConfigData must be used within a ConfigProvider')
  }
  return context
}

function App() {
  return <QueryClientProvider client={queryClient}>
      <Auth />
  </QueryClientProvider>
}

function Auth() {
  const authContext = window.location.hostname === "localhost" ?
      {
        status: "success",
        data: {
          "logoutURL": "https://dev.annieadvisor.com/simplesaml/module.php/core/as_logout.php?AuthId=sp-google&ReturnTo=https%3A%2F%2Fdev.annieadvisor.com%2Fapi%2Fauth.php",
          "firstname": "localhost",
          "lastname": "developer",
          "uid": "devannie",
          "status": "OK"
        }
      }
      : AuthCheck()

  const success = authContext.status === "success" && authContext.data.hasOwnProperty("uid") && authContext.data.hasOwnProperty("status") && authContext.data.status === "OK"
  const error = authContext.status === "error"

  if (success) {
    return <AuthSuccess authContext={authContext.data}/>
  }
  if (error) {
    if (typeof authContext.error.response.data.loginURL !== undefined) {
      window.location.href = authContext.error.response.data.loginURL
    } else {
      return <AuthError/>
    }
  }

  return <Loading/>
}

function AuthSuccess({authContext}) {
  const getUser = GetUser(authContext.uid)
  const getContacts = GetContacts() // Check if user has user access
  const getUserSurveys = GetUserSurvey() // Check if use is coordinator or superuser
  const getInstanceLanguage = GetInstanceLanguage()
  const getLanguageOptions = GetLanguageOptions()
  const getUserGroupData = GetUserGroupData()

  const isLoading = getUser.isLoading || getContacts.isLoading || getUserSurveys.isLoading || getInstanceLanguage.isLoading || getLanguageOptions.isLoading || getUserGroupData.isLoading
  const isError = getUser.isError || getContacts.isError || getInstanceLanguage.isError || getLanguageOptions.isError || getUserGroupData.isError // getUserSurveys is excluded since it returns error for normal support providers

  const unAuthorized = isError && getContacts.error?.response?.status === 401
  const notSuperuserOrCoordinator = getUserSurveys.isError && getUserSurveys.error?.response?.status === 401
  const noAnnieUser = getUser.data?.length < 1

  let content

  if (isLoading) {
    return <Loading />
  }

  if (isError) {
    if (unAuthorized) {
      content = <SomethingWentWrong errorType={"NO_RIGHTS_ANNIEUSER"} />
    } else {
      content = <SomethingWentWrong errorType={"LOADING_ERROR"} />
    }
  } else if (noAnnieUser) {
    content = <SomethingWentWrong errorType={"MISSING_ANNIEUSER"} />
  } else {
    content = <UserInformation userContext={getUser.data[0]}
                               notSuperuserOrCoordinator={notSuperuserOrCoordinator}
                               languageContext={getInstanceLanguage?.data[0]?.value}
                               userGroupData={getUserGroupData?.data}
    />
  }

  return <AuthProvider authContext={authContext}>
    <LocaleWrapper language={getInstanceLanguage?.data[0]?.value || ""} languageOptions={getLanguageOptions?.data[0]?.value || []}>
      {content}
    </LocaleWrapper>
  </AuthProvider>
}

function UserInformation({userContext, notSuperuserOrCoordinator, languageContext, userGroupData}) {
  const superuser = userContext.superuser
  const coordinator = !superuser && !notSuperuserOrCoordinator
  const showGroupView = (userGroupData.hasOwnProperty("campaigns") && userGroupData.hasOwnProperty("noCampaignSurveys")) && (userGroupData.campaigns?.length > 0 || userGroupData.noCampaignSurveys?.length > 0)
  let data = _.cloneDeep(userContext)
  data["coordinator"] = coordinator
  data["groupView"] = showGroupView

  return <InstanceWrapper languageContext={languageContext}>
    <CurrentUserProvider userContext={data}>
        <div className={"app"}>
          <Content
            userGroupData={userGroupData}
          />
        </div>
    </CurrentUserProvider>
  </InstanceWrapper>
}

function InstanceWrapper({children, languageContext}) {
  const getSupportRolesData = GetSupportRoles()
  const loading = getSupportRolesData.isLoading
  let configs = {
    roles: getSupportRolesData.isSuccess ? getSupportRolesData.data[0] : ""
  }
  return loading ? <Loading/> : <LanguageProvider context={languageContext}>
    <ConfigProvider context={configs}>
    {children}
    </ConfigProvider>
  </LanguageProvider>
}

function SomethingWentWrong({errorType}) {
  const hostname = window.location.hostname
  const instanceEmail = hostname.indexOf(".") > -1 ? hostname.substring( 0, hostname.indexOf(".") ) +"@annieadvisor.com" : hostname +"@annieadvisor.com"
  return <div className={"app"}>
    <div className={"content"}>
      <div className={"header"}>
        <span className={"logo"}>
          <Logo />
        </span>
        <Profile />
      </div>
      <div className={"main"}>
        <main className={"support-requests-wrapper"}>
        <div className={"support-requests-container"}>
          <div className={"no-requests-container"}>
              {errorType === "MISSING_ANNIEUSER" &&
              <>
                <div id={"no-requests"}>
                  <Face />
                </div>
                <h4>
                  <FormattedMessage
                      id={"noAnnieUser"}
                      defaultMessage={"Successfully logged in!{br}{br}You have not yet been granted access privileges to Annie. If you think this is a mistake, please contact"}
                      values={{br: <br/>}}/> <a href={"mailto:"+instanceEmail}>{instanceEmail}</a>
                </h4>
              </>
              }
              {errorType === "LOADING_ERROR" &&
              <h4>
                <FormattedMessage
                    id={"loadingError"}
                    defaultMessage={"Loading error. Can't access database. Please try again later."}
                />
              </h4>
              }
              {errorType === "NO_RIGHTS_ANNIEUSER" &&
              <>
                <div id={"no-requests"}>
                  <Face />
                </div>
                <h4>
                  <FormattedMessage
                      id={"annieUserNoRights"}
                      defaultMessage={"Successfully logged in!{br}{br}You have not yet been added to any campaigns. If you think this is a mistake, please contact"}
                      values={{br: <br/>}}/> <a href={"mailto:"+instanceEmail}>{instanceEmail}</a>
                </h4>
              </>
              }
          </div>
        </div>
        </main>
      </div>
    </div>
  </div>
}

function AuthError() {
  const hostname = window.location.hostname
  const instanceEmail = hostname.indexOf(".") > -1 ? hostname.substring( 0, hostname.indexOf(".") ) +"@annieadvisor.com" : hostname +"@annieadvisor.com"

  return <div className={"app"}>
    <div className={"content center login"}>
      <div>
        <div className={"logo"}>
          <CloudLogo />
        </div>
        <h1>Sorry. Seems like an authentication error.</h1>
        <p>Please contact <a href={"mailto:"+instanceEmail}>{instanceEmail}</a></p>
      </div>
    </div>
  </div>
}

export function Loading() {
  return <div className={"app"}>
    <div className={"content center login"}>
        <div className={"loader"}>
          <Logo />
          <div className={"loader-container"}>
            <div className={"loader-bar"}/>
          </div>
        </div>
    </div>
  </div>
}

export default App;
